// Key Indicators Panel — 3-column: indicator | raw value | algo interpretation
//
// Every signal the sleeve roster uses, with live data and threshold-based
// gate/signal interpretations. New sleeves automatically benefit because
// the panel reads from the same data sources as the runners.

import { useQuery } from "@tanstack/react-query";
import { portfolio, predictive } from "../api/endpoints";
import type { MarketWidgets, PredictiveOverview } from "../api/types";

const REFRESH_MS = 30_000;

// ── Threshold interpreters ─────────────────────────────────────────────

function vixInterpretation(v: number): string {
  if (v > 30) return `>30 — MGC LONG fear gate TRIGGERED · overlay VIX BLOCKED`;
  if (v > 25) return `>25 — Elevated fear zone · MGC gate ARMED`;
  if (v > 20) return `>20 — Above average · monitor escalations`;
  if (v > 15) return `>15 — Normal regime · no VIX-driven signal`;
  return `<15 — Low vol · complacency possible if 10d SMA<13.5`;
}

function vxnRatioInterpretation(ratio: number): string {
  if (ratio > 1.15) return `>1.15 — VXNTECH TRIGGERED · NVDA/NDX fear gate active`;
  if (ratio > 1.05) return `>1.05 — VXN elevated vs VIX · tech outperforming SPX fear`;
  return `<1.05 — Normal tech/SPX fear alignment · gate OPEN`;
}

function dxyInterpretation(dxy: number): string {
  if (dxy > 105) return `>105 — USD STRONG · risk-off pressure on equities/gold`;
  if (dxy > 102) return `>102 — USD firming · monitor liquidity squeeze gate`;
  if (dxy < 96) return `<96 — USD WEAK · risk-on tailwind for equities`;
  return `96-102 — Neutral zone · no USD-driven pressure on sleeves`;
}

function yieldSpreadInterpretation(bp: number): string {
  if (bp < 0) return `INVERTED ${Math.abs(bp).toFixed(0)}bp — recession signal · risk-off`;
  if (bp < 25) return `FLAT ${bp.toFixed(0)}bp — growth concerns · monitor`;
  if (bp > 80) return `STEEP ${bp.toFixed(0)}bp — growth positive · risk-on`;
  return `${bp.toFixed(0)}bp — Normal curve · no macro signal`;
}

function hyoasInterpretation(oas: number): string {
  if (oas > 4.0) return `>400bp — CREDIT STRESS · flight-to-safety MGC TRIGGERED`;
  if (oas > 2.5) return `>250bp — Credit fear ELEVATED · HY OAS gate ARMED`;
  return `<250bp — Credit normal · no HY OAS signal`;
}

function tedInterpretation(ted: number): string {
  if (ted > 1.0) return `>1.0% — Funding STRESS · systemic risk mode`;
  if (ted > 0.5) return `>0.5% — Funding elevated · counterparty risk rising`;
  return `<0.5% — Normal funding · no TED signal from any sleeve`;
}

function vvixInterpretation(vvix: number): string {
  if (vvix > 130) return `>130 — Vol-of-vol HIGH · VIX peak unwind promotions`;
  if (vvix > 110) return `>110 — Elevated · VVIX promo gate ARMED`;
  return `≤110 — Normal vol-of-vol · no VVIX gate signal`;
}

function skewInterpretation(skew: number): string {
  if (skew < 120) return `<120 — COMPLACENCY · SHORT MGC Mon-Fri signal ARMED`;
  if (skew > 150) return `>150 — Tail risk elevated · put demand high`;
  return `120-150 — Normal range · no SKEW gate signal`;
}

function jpyInterpretation(zScore: number): string {
  const zAbs = Math.abs(zScore);
  if (zAbs > 2.0) return `JPY z=${zScore.toFixed(1)} — extreme move · flight-to-safety gate may fire`;
  if (zAbs > 1.5) return `JPY z=${zScore.toFixed(1)} — elevated · monitor DXY-JPY gate`;
  return `JPY z=${zScore.toFixed(1)} — normal range · no DXY-JPY signal`;
}

function gexInterpretation(netGex: number | null, totalGamma: number | null, pcr: number | null): string {
  if (netGex == null) return "No GEX data — Yahoo fetch failed or market closed (daily snapshot cron unaffected)";
  const b = (v: number) => Math.abs(v) >= 1e12 ? `$${(Math.abs(v) / 1e12).toFixed(2)}T` : `$${(Math.abs(v) / 1e9).toFixed(1)}B`;
  const meta = [`|γ| ${totalGamma != null ? b(totalGamma) : "—"}`, pcr != null ? `PCR ${pcr.toFixed(2)}` : null].filter(Boolean).join(" · ");
  if (netGex > 0) return `Net GEX +${b(netGex)} — dealers net LONG gamma, hedge against moves, pinning bias (${meta})`;
  return `Net GEX −${b(netGex)} — dealers net SHORT gamma, moves amplify${Math.abs(netGex) > 5e11 ? ", squeeze risk ELEVATED" : ""} (${meta})`;
}

// ── Sub-components ──────────────────────────────────────────────────────

function IndicatorRow({
  label,
  value,
  interpretation,
  color,
}: {
  label: string;
  value: string | number | null;
  interpretation: string;
  color?: string;
}) {
  const valueStr = value != null && value !== "" ? String(value) : "—";
  const rowColor = value == null ? "var(--fg-muted)" : (color ?? "var(--fg-primary)");
  return (
    <tr style={{ borderBottom: "1px solid var(--border)" }}>
      <td style={{ padding: "1px 4px", fontSize: 10, color: "var(--fg-muted)", fontFamily: "var(--sans)", whiteSpace: "nowrap", letterSpacing: "0.02em" }}>
        {label}
      </td>
      <td className="num mono" style={{ padding: "1px 4px", fontSize: 11, fontWeight: 600, color: rowColor }}>
        {valueStr}
      </td>
      <td style={{ padding: "1px 4px", fontSize: 9, color: "var(--fg-secondary)", lineHeight: 1.3 }}>
        {interpretation}
      </td>
    </tr>
  );
}

function SectionHeader({ label }: { label: string }) {
  return (
    <tr>
      <td colSpan={3} style={{
        padding: "3px 4px 1px 4px",
        fontSize: 8,
        fontWeight: 600,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "var(--fg-muted)",
        fontFamily: "var(--sans)",
        background: "var(--bg-elev-2)",
        position: "sticky",
        top: 0,
        zIndex: 1,
      }}>
        {label}
      </td>
    </tr>
  );
}

// ── Main component ──────────────────────────────────────────────────────

export function KeyIndicatorsPanel() {
  const marketQ = useQuery<MarketWidgets>({
    queryKey: ["portfolio", "marketWidgets"],
    queryFn: () => portfolio.marketWidgets(),
    refetchInterval: REFRESH_MS,
  });

  const predictiveQ = useQuery<PredictiveOverview>({
    queryKey: ["predictive", "overview"],
    queryFn: () => predictive.overview(),
    refetchInterval: REFRESH_MS,
  });

  const mkt = marketQ.data;
  const pred = predictiveQ.data;

  const vix = mkt?.vix ?? null;
  const vxn = mkt?.vxn ?? null;
  const dxy = mkt?.dxy ?? null;
  const vvix = mkt?.vvix ?? null;
  const skew = mkt?.skew ?? null;
  const hy_oas = mkt?.hy_oas ?? null;
  const ted = mkt?.ted ?? null;
  const jpy = mkt?.jpy ?? null;
  const vix_sma10 = mkt?.vix_sma10 ?? null;
  const vix_vxv = mkt?.vix_vxv ?? null;
  const dxy_jpy_z = mkt?.dxy_jpy_z ?? null;
  const yields = mkt?.treasury_yields ?? null;
  const vixGateBlocked = mkt?.vix_gate_blocked ?? false;
  const volRegime = pred?.vol_regime?.regime ?? null;
  const gexNetDelta = pred?.gex_delta?.net_delta ?? null; // signed net GEX (call γ − put γ), engine DTO field name is net_delta
  const gexTotalGamma = pred?.gex_delta?.total_gamma ?? null; // Σ|gamma| magnitude, always ≥ 0
  const gexPcr = pred?.gex_delta?.put_call_ratio ?? null;

  const y2y10ySpread = (yields?.y2y != null && yields?.y10y != null) ? (yields.y10y - yields.y2y) : null;

  const vixCol = vix != null && vix > 30 ? "var(--bad)" : vix != null && vix > 20 ? "var(--warn)" : vix != null && vix < 15 ? "var(--good)" : undefined;

  return (
    <div className="card">
      <h2>Key Indicators</h2>
      {/* <div style={{ fontSize: 9, color: "var(--fg-muted)", marginBottom: 4, fontFamily: "var(--sans)" }}> */}
      <div className="muted small" style={{marginBottom: 4}}>indicator · raw value · algo decision</div>
      <div className="scroll-vert" style={{ scrollbarWidth: "thin" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: "22%" }} />
            <col style={{ width: "18%" }} />
            <col style={{ width: "60%" }} />
          </colgroup>
          <tbody>
            {/* ── FEAR (MGC Long Gates) ── */}
            <SectionHeader label="Fear (MGC Long Gates)" />

            <IndicatorRow
              label="VIX"
              value={vix != null ? vix.toFixed(2) : "—"}
              interpretation={vix != null ? vixInterpretation(vix) : "Awaiting data"}
              color={vixCol}
            />
            <IndicatorRow
              label="VIX/VXV"
              value={vix_vxv != null ? vix_vxv.toFixed(3) : "—"}
              interpretation={vix_vxv != null
                ? (vix_vxv > 1.05 ? `>1.05 — Vol-slope INVERTED · fear regime · VXNTECH gate ARMED`
                  : vix_vxv > 1.0 ? `>1.0 — VIX above VXV · short-term fear elevated`
                  : `<1.0 — Normal term structure · no vol-slope signal`)
                : "Awaiting data"}
              color={vix_vxv != null && vix_vxv > 1.05 ? "var(--bad)" : vix_vxv != null && vix_vxv > 1.0 ? "var(--warn)" : undefined}
            />
            <IndicatorRow
              label="VVIX"
              value={vvix != null ? vvix.toFixed(1) : "—"}
              interpretation={vvix != null ? vvixInterpretation(vvix) : "Awaiting data"}
              color={vvix != null && vvix > 130 ? "var(--bad)" : vvix != null && vvix > 110 ? "var(--warn)" : undefined}
            />
            <IndicatorRow
              label="HY OAS"
              value={hy_oas != null ? (hy_oas * 100).toFixed(0) : "—"}
              interpretation={hy_oas != null ? hyoasInterpretation(hy_oas) : "Awaiting data"}
              color={hy_oas != null && hy_oas > 4.0 ? "var(--bad)" : hy_oas != null && hy_oas > 2.5 ? "var(--warn)" : undefined}
            />
            <IndicatorRow
              label="TED Spread"
              value={ted != null ? ted.toFixed(2) : "—"}
              interpretation={ted != null ? tedInterpretation(ted) : "Awaiting data"}
              color={ted != null && ted > 1.0 ? "var(--bad)" : ted != null && ted > 0.5 ? "var(--warn)" : undefined}
            />
            <IndicatorRow
              label="DXY-JPY z-score"
              value={dxy_jpy_z != null ? dxy_jpy_z.toFixed(2) : "—"}
              interpretation={dxy_jpy_z != null ? jpyInterpretation(dxy_jpy_z) : "Awaiting data"}
              color={dxy_jpy_z != null && Math.abs(dxy_jpy_z) > 2.0 ? "var(--bad)" : dxy_jpy_z != null && Math.abs(dxy_jpy_z) > 1.5 ? "var(--warn)" : undefined}
            />

            {/* ── VOLATILITY & STRUCTURE ── */}
            <SectionHeader label="Volatility & Structure" />

            <IndicatorRow
              label="VXN (NDX Vol)"
              value={vxn != null ? vxn.toFixed(2) : "—"}
              interpretation={vxn != null
                ? (vxn > 30 ? ">30 — Elevated NDX fear · tech selloff risk"
                  : vxn > 20 ? ">20 — Above average NDX vol"
                  : "<20 — Normal NDX vol · no tech-fear signal")
                : "—"}
              color={vxn != null && vxn > 30 ? "var(--bad)" : vxn != null && vxn > 22 ? "var(--warn)" : undefined}
            />
            <IndicatorRow
              label="VXN/VIX"
              value={vix_vxv != null ? vix_vxv.toFixed(3) : "—"}
              interpretation={vix_vxv != null ? vxnRatioInterpretation(vix_vxv) : "—"}
              color={vix_vxv != null && vix_vxv > 1.15 ? "var(--bad)" : vix_vxv != null && vix_vxv > 1.05 ? "var(--warn)" : undefined}
            />
            <IndicatorRow
              label="VXN/VIX Gate"
              value={vix_vxv != null ? (vix_vxv > 1.15 ? "TRIGGERED" : vix_vxv > 1.05 ? "ELEVATED" : "OPEN") : "—"}
              interpretation={vix_vxv != null && vix_vxv > 1.15
                ? "VXNTECH gate ACTIVE · fear-cond opening drift, VXV squeeze sleeves gate on this"
                : vix_vxv != null && vix_vxv > 1.05
                ? "Gate borderline · sleeves require >1.15, currently ELEVATED"
                : "Gate OPEN · no tech/SPX divergence"}
              color={vix_vxv != null && vix_vxv > 1.15 ? "var(--bad)" : vix_vxv != null && vix_vxv > 1.05 ? "var(--warn)" : vix_vxv != null ? "var(--good)" : undefined}
            />

            {/* ── TAIL RISK / COMPLACENCY ── */}
            <SectionHeader label="Tail Risk & Complacency" />

            <IndicatorRow
              label="SKEW"
              value={skew != null ? skew.toFixed(1) : "—"}
              interpretation={skew != null ? skewInterpretation(skew) : "Awaiting data"}
              color={skew != null && skew < 120 ? "var(--bad)" : skew != null && skew > 150 ? "var(--warn)" : undefined}
            />
            <IndicatorRow
              label="VIX 10d SMA"
              value={vix_sma10 != null ? vix_sma10.toFixed(2) : "—"}
              interpretation={vix_sma10 != null
                ? (vix_sma10 < 13.5 && skew != null && skew < 120
                  ? `<13.5 + SKEW<120 — COMPLACENCY signal · SHORT MGC Mon-Fri ARMED`
                  : vix_sma10 < 13.5
                  ? `<13.5 — Complacency threshold met (VIX) · waiting for SKEW<120`
                  : `≥13.5 — No complacency · VIX too elevated for SHORT MGC`)
                : "Awaiting data"}
              color={vix_sma10 != null && vix_sma10 < 13.5 ? "var(--warn)" : undefined}
            />

            {/* ── REGIME & OVERLAY ── */}
            <SectionHeader label="Regime & Overlay" />

            <IndicatorRow
              label="VIX Gate"
              value={vixGateBlocked ? "BLOCKED" : "OPEN"}
              interpretation={vixGateBlocked
                ? "OVERLAY ENGAGED — no new entries · price dist at extremes"
                : "Gate OPEN — all sleeves free to enter"}
              color={vixGateBlocked ? "var(--bad)" : "var(--good)"}
            />
            <IndicatorRow
              label="Vol Regime"
              value={volRegime ?? "—"}
              interpretation={volRegime === "high"
                ? "HIGH — sleeves with vol-sensitive sizing may reduce exposure"
                : volRegime === "normal"
                ? "Normal — standard parameters active across all sleeves"
                : "Awaiting classification"}
              color={volRegime === "high" ? "var(--bad)" : volRegime === "normal" ? "var(--good)" : undefined}
            />
            <IndicatorRow
              label="GEX Net Gamma"
              value={gexNetDelta != null ? `$${Math.abs(gexNetDelta) >= 1e12 ? (gexNetDelta / 1e12).toFixed(2) + "T" : (gexNetDelta / 1e9).toFixed(1) + "B"}` : "—"}
              interpretation={gexInterpretation(gexNetDelta, gexTotalGamma, gexPcr)}
              color={gexNetDelta != null ? (gexNetDelta > 0 ? "var(--good)" : "var(--bad)") : "var(--fg-muted)"}
            />

            {/* ── MACRO ── */}
            <SectionHeader label="Macro" />

            <IndicatorRow
              label="DXY"
              value={dxy != null ? dxy.toFixed(3) : "—"}
              interpretation={dxy != null ? dxyInterpretation(dxy) : "—"}
              color={dxy != null && dxy > 105 ? "var(--bad)" : dxy != null && dxy < 96 ? "var(--warn)" : undefined}
            />
            <IndicatorRow
              label="USD/JPY"
              value={jpy != null ? jpy.toFixed(2) : "—"}
              interpretation={jpy != null
                ? (jpy > 160 ? `>160 — JPY WEAK · DXY/JPY carry pressure · flight-to-MGC gate may fire on unwind`
                  : jpy < 130 ? `<130 — JPY STRONG · safe-haven demand · risk-off signal`
                  : `130-160 — Normal range · no JPY-driven sleeve signal`)
                : "Awaiting data"}
              color={jpy != null && jpy > 160 ? "var(--warn)" : jpy != null && jpy < 130 ? "var(--bad)" : undefined}
            />
            <IndicatorRow
              label="2Y Yield"
              value={yields?.y2y != null ? yields.y2y.toFixed(2) : "—"}
              interpretation={yields?.y2y != null
                ? (yields.y2y > 5.0 ? ">5% — Restrictive · rate hike pressure on risk"
                  : yields.y2y > 4.5 ? ">4.5% — Elevated · carry negative for risk"
                  : yields.y2y > 3.5 ? "~4% — Normal for current Fed posture"
                  : "<3.5% — Accommodative · cuts expected")
                : "—"}
            />
            <IndicatorRow
              label="10Y Yield"
              value={yields?.y10y != null ? yields.y10y.toFixed(2) : "—"}
              interpretation={yields?.y10y != null
                ? (yields.y10y > 5.0 ? ">5% — Bond selloff · equity valuation pressure"
                  : yields.y10y > 4.5 ? ">4.5% — Elevated · growth concern zone"
                  : yields.y10y > 3.5 ? "~4-4.5% — Neutral"
                  : "<3.5% — Low yields · accommodative")
                : "—"}
            />
            <IndicatorRow
              label="2Y/10Y Spread"
              value={y2y10ySpread != null ? (y2y10ySpread * 100).toFixed(0) + "bp" : "—"}
              interpretation={y2y10ySpread != null ? yieldSpreadInterpretation(y2y10ySpread * 100) : "—"}
              color={y2y10ySpread != null ? (y2y10ySpread < 0 ? "var(--bad)" : y2y10ySpread * 100 < 25 ? "var(--warn)" : "var(--good)") : undefined}
            />
          </tbody>
        </table>
      </div>
    </div>
  );
}